import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { SkeletonTable } from '@/components/ui/skeleton'
import { useServiceWithAddonConfigPrices } from '@/modules/Service/services'
import { ServiceInformationProps } from '@/modules/Service/types'
import { useServiceDetailActions } from '@/modules/Service/utils/service-detail'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertError } from 'vnetwork-ui'
import { createCDNColumns, transformCDNData, type CDNRow } from './cdn-config'

const Information: React.FC<ServiceInformationProps> = ({ service }) => {
    const { t } = useTranslation()
    const { data, isLoading, isError, error, isPending } =
        useServiceWithAddonConfigPrices(service?.service_id)

    // Check if any configoption has price
    const hasPrice = useMemo(() => {
        return !!data?.configoptions?.some(
            (configoption) => configoption.commitment_value > 0
        )
    }, [data])

    // Transform data to rows
    const rows = useMemo(() => {
        if (!service || !data) return []
        return transformCDNData({ service, data })
    }, [service, data])

    // Create columns
    const columns = useMemo(() => createCDNColumns(t, hasPrice), [t, hasPrice])

    // Row actions (confirm/reject/cancel) — same as other templates. On CDN the
    // main service is normally already active (activated via its ticket workflow),
    // so confirm only lights up for waiting-confirm addon rows, which confirm directly.
    const actions = useServiceDetailActions<CDNRow>(t)

    // Show stopped date column if service has stopped date
    const visibleColumns = useMemo(() => {
        return columns.map((col) => ({
            ...col,
            hidden:
                col.key === 'service_stopped_date'
                    ? !service?.service_stopped_date
                    : col.hidden,
        }))
    }, [columns, service?.service_stopped_date])

    if (isError) return <AlertError error={error} />
    if (isLoading || isPending) <SkeletonTable />

    return (
        <div className="space-y-2">
            <DataTableCustomized<CDNRow>
                columns={visibleColumns}
                rows={rows}
                rowKey={(row) =>
                    String(row?.service_configoption_id || row?.id)
                }
                size="small"
                resizable
                emptyMessage={t('no-data', { defaultValue: 'No data' })}
                actions={actions}
            />
        </div>
    )
}

export default Information
